import { DIALOG_DATA, Dialog, DialogRef } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { portalConst } from '@app/config';
import { AlertService, NotificationService } from '@app/core';
import { Attachment } from '@app/core/models';
import { AttachmentService } from '@app/core/services/attachment.service';
import { SystematicService } from '@app/core/services/systematic.service';
import { UserService } from '@app/core/services/user.service';
import { SHARED_MODULES } from '@app/shared';
import { TextEditorComponent } from '@app/shared/modules/text-editor/text-editor.component';
import { UploadFileComponent } from '@app/shared/modules/upload-file/upload-file.component';
import { currentAcademicPeriod, notNull } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  filter,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { ModalFileViewerComponent } from '../modal-file-viewer/modal-file-viewer.component';

@Component({
  imports: [SHARED_MODULES, TextEditorComponent, UploadFileComponent],
  templateUrl: './modal-systematic-new.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalSystematicNewComponent implements AfterViewInit, OnDestroy {
  public readonly data = inject(DIALOG_DATA);
  public readonly dialog = inject(Dialog);
  public readonly dialogRef = inject<DialogRef<boolean>>(DialogRef);
  private readonly _destroy = inject(DestroyRef);
  private readonly _store = inject<Store<AppState>>(Store);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _translateService = inject(TranslateService);
  private readonly _alertService = inject(AlertService);
  private readonly _attachmentService = inject(AttachmentService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _systematicService = inject(SystematicService);
  private readonly _userService = inject(UserService);
  public readonly ckeditorUploadImageUrl = `${portalConst.api.usuario}/v2/upload/ckeditor-image?module=communications`;

  public form: FormGroup;
  public submitted$ = new BehaviorSubject<boolean>(false);
  public entityId$: Observable<number>;
  public group$: Observable<any[]>;
  public allGroups$: Observable<any[]>;
  public academicPeriod$: Observable<Date[]>;
  public resetAttachment: boolean;
  public loading = signal(false);

  public config: DropzoneConfigInterface = {
    clickable: true,
    url: `${portalConst.api.eclass}/v1/upload/attachment`,
    createImageThumbnails: false,
    maxFiles: 10,
    maxFilesize: 30,
    acceptedFiles: '.pdf, .mp3, .mp4, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .jpg, .jpeg, .png, .pdf, .txt',
    previewsContainer: false,
    params: { folder: 'links' },
  };

  constructor() {
    this.initForm();
    this.load();
  }

  public ngAfterViewInit() {
    if (this.data?.systematic) {
      const groupIds = this.data.systematic?.groups?.map((groups) => groups.id);
      this.form.patchValue({
        ...this.data.systematic,
        groups: groupIds,
      });
      this._cdRef.detectChanges();
    }
  }

  public get enableDropzone(): boolean {
    return this.form.get('attachments').value?.length >= this.config.maxFiles;
  }

  public ngOnDestroy() {
    this.submitted$.complete();
  }

  public onCancel() {
    this.form.reset();
    this.dialogRef.close(this.resetAttachment);
  }

  public onSave() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.save();
    }
  }

  public icon(extension): string {
    return '/assets/images/files/' + extension + '.svg';
  }

  public roundedNumber(numero: number): number {
    return numero > 1000 ? Math.round(numero / 1000) : Math.round(numero);
  }

  public onUpload(data: any) {
    this.form.get('attachments').setValue([...(this.form.get('attachments')?.value || []), data]);
    this.form.markAsDirty();
    this.form.markAsTouched();
  }

  public disableSendUpload(data) {
    if (!!data.uploading) {
      this.submitted$.next(true);
    }
  }

  public enableSendOnUpload(data) {
    if (!!data.finished) {
      this.submitted$.next(false);
    }
  }

  public removeFile(removedFile: Attachment) {
    this._alertService
      .confirm({
        message: this._translateService.instant('eclass.tarefa.remover-arquivo'),
      })
      .pipe(
        filter(Boolean),
        switchMap(() => (removedFile ? this._attachmentService.deleteAttachment(removedFile.id) : EMPTY)),
        catchError(() => of({ ret: 0 })),
      )
      .subscribe((res) => {
        if (res.ret === 1) {
          const attachments = this.form.value.attachments.filter((file: Attachment) => file !== removedFile);
          this.form.get('attachments').setValue(attachments);
          this._notificationService.success(this._translateService.instant('sistematica.erro-excluir-arquivo'));
          this.resetAttachment = true;
          this._cdRef.markForCheck();
        } else {
          this._notificationService.error(this._translateService.instant('sistematica.erro-excluir-arquivo'));
        }
      });
  }

  public onOpen(data: Attachment) {
    this.dialog.open(ModalFileViewerComponent, { data });
  }

  public getSelectedGroups(allGroups: any[]): any[] {
    const selectedIds = this.form.get('groups').value || [];
    return (allGroups || []).filter((group) => selectedIds.includes(group.id));
  }

  public removeGroup(id: any): void {
    const selectedIds = this.form.get('groups').value || [];
    const newIds = selectedIds.filter((selectedId) => selectedId !== id);
    this.form.get('groups').setValue(newIds);
    this._cdRef.markForCheck();
  }

  private initForm() {
    this.form = this._formBuilder.group({
      id: undefined,
      title: [undefined, Validators.required],
      description: [undefined, Validators.required],
      groups: [undefined, Validators.required],
      attachments: undefined,
      date_start: [new Date(), Validators.required],
      date_end: [new Date(), Validators.required],
      type: 1,
    });
  }

  private load() {
    const activeUser$ = this._store.select(AppSelectors.ActiveUser).pipe(notNull(), takeUntilDestroyed(this._destroy));
    this.entityId$ = activeUser$.pipe(map(({ entidade_id }) => entidade_id));

    this.allGroups$ = activeUser$.pipe(switchMap(({ entidade_id }) => this._userService.getGroups(entidade_id)));

    this.group$ = combineLatest([
      this.allGroups$,
      this.form.get('groups').valueChanges.pipe(startWith(this.form.get('groups').value)),
    ]).pipe(
      map(([groups, selectedIds]) => {
        const ids = selectedIds || [];
        return (groups || []).filter((group) => !ids.includes(group.id));
      }),
    );

    this.academicPeriod$ = activeUser$.pipe(map((activeUser) => currentAcademicPeriod(activeUser)));
  }

  private save() {
    this._alertService
      .confirm({
        message: !!this.form.value.id
          ? this._translateService.instant(`sistematica.alterar-sistematica`)
          : this._translateService.instant(`sistematica.salvar-sistematica`),
      })
      .pipe(
        filter((result) => !!result),
        tap(() => this.loading.set(true)),
        switchMap(() =>
          (!!this.form.value.id
            ? this._systematicService.update(this.form.get('id').value, this.form.value)
            : this._systematicService.store(this.form.value)
          ).pipe(
            catchError(() => {
              this.loading.set(false);
              return of({ ret: 0 });
            }),
          ),
        ),
        tap(() => this.loading.set(false)),
      )
      .subscribe((res) => {
        if (res.ret === 1) {
          this._notificationService.success(this._translateService.instant(`sistematica.sistematica-salva`));
          this.dialogRef.close(true);
        } else {
          this._notificationService.error(this._translateService.instant(`sistematica.erro-salvar-sistematica`));
        }
      });
  }
}
